import { css, StyleDeclarationValue } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useMediaQuery } from 'react-responsive';
import { useAdminUserSession } from '../../../models/hooks/adminStateHooks';
import { AdminPermission, AdminUserSessionContext } from '../../../viewmodels/AdminViewModels';
import { NavBar } from '../../../web/components/NavBar';
import { INavBarLink } from '../../../web/components/NavBarLink';
import { ScrollView } from '../../../web/components/ScrollView';
import { SparklesIcon } from '../../../web/components/svgs/icons/SparklesIcon';
import { Masthead } from '../../components/Masthead';
import { NavHeaderBackground } from '../../components/NavHeaderBackground';
import { isResellerSession } from '../../contexts/ResellerContext';
import { getSessionAdminAccountsUrl } from '../account/Accounts';
import { CompactWidth, styleSheet } from './styles';

const getNavBarLinks = (userSession: AdminUserSessionContext, isReseller: boolean): INavBarLink[] => {
	// Resellers only see Accounts
	if (isReseller) {
		return [
			{
				linkName: 'Accounts',
				path: getSessionAdminAccountsUrl(),
			},
		];
	}

	const hasManageTemplatesPermission = userSession.hasPermission(AdminPermission.ManageTemplates);
	const hasManageSurveyTemplatesPermission = userSession.hasPermission(AdminPermission.ManageSurveyTemplates);
	const hasManageAIAgentTemplatesPermission = userSession.hasPermission(AdminPermission.ManageAIAgentTemplates);
	const hasOnlyForDevelopersPermission = userSession.hasPermission(AdminPermission.OnlyForDevelopers);
	const hasCanViewOpenInvoicesPermission =
		userSession.hasPermission(AdminPermission.ViewOpenInvoices) ||
		userSession.hasPermission(AdminPermission.OnlyForDevelopers);

	return [
		{
			linkName: 'Accounts',
			path: getSessionAdminAccountsUrl(),
		},
		{
			linkName: 'Reports',
			path: '/reports',
		},
		...(hasCanViewOpenInvoicesPermission
			? [
					{
						linkName: 'Open Invoices',
						path: '/billing/openInvoices',
					},
				]
			: []),
		...(hasManageTemplatesPermission
			? [
					{
						linkName: 'Campaigns',
						path: '/campaigns',
					},
					{
						linkName: 'Automations',
						path: '/automations',
					},
				]
			: []),
		...(hasManageSurveyTemplatesPermission
			? [
					{
						linkName: 'Surveys',
						path: '/surveys',
					},
				]
			: []),
		...(hasManageAIAgentTemplatesPermission
			? [
					{
						linkName: 'AI Agents',
						icon: <SparklesIcon height={18} width={18} />,
						path: '/ai-agent-templates',
					},
				]
			: []),
		{
			linkName: 'Tools',
			path: '/tools',
		},
		...(hasOnlyForDevelopersPermission
			? [
					{
						linkName: 'DevTools',
						path: '/devtools',
					},
				]
			: []),
	];
};

function _AdminDashboardPageLayout({
	children,
	compactNav = false,
	sidebarChildren,
	contentStyles = [],
}: {
	children: React.ReactNode;
	compactNav?: boolean;
	sidebarChildren?: React.ReactNode;
	contentStyles?: StyleDeclarationValue[];
}) {
	const userSession = useAdminUserSession();
	const isReseller = isResellerSession(userSession);
	const isWideScreen = useMediaQuery({ minWidth: CompactWidth });
	const shouldCollapseNav = !isWideScreen || compactNav;
	return (
		<div
			className={`admin-container ${css(styleSheet.container)} ${
				shouldCollapseNav ? 'admin-container-compact-nav' : ''
			}`}
		>
			<NavBar
				className={`admin-container-nav ${css(shouldCollapseNav ? styleSheet.compactNav : styleSheet.nav)}`}
				compactLayout={shouldCollapseNav}
				header={<NavHeaderBackground compactLayout={shouldCollapseNav} styles={[styleSheet.navHeaderBackground]} />}
				links={getNavBarLinks(userSession, isReseller)}
			/>
			<div className={css(styleSheet.rightContainer)}>
				<div className={css(styleSheet.containerContent, ...contentStyles)}>
					<Masthead className={`admin-container-header ${css(styleSheet.masthead)}`} />
					<ScrollView
						className={`admin-container-right-container-content ${css(styleSheet.rightContainerScroller)}`}
						contentClassName={css(styleSheet.rightContainerScrollerContent)}
					>
						{children}
					</ScrollView>
				</div>
			</div>
			{sidebarChildren}
		</div>
	);
}

export const AdminDashboardPageLayout = observer(_AdminDashboardPageLayout);
